import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import { defaultAddEditDocumentDialogState } from "components/Document";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  EditMode,
  FormState,
} from "components/Document/AddEditDocumentDialog";
import { DocumentPreview } from "components/Document/shared/DocumentPreview";
import { DocumentListItem } from "components/DocumentListItem";
import { ReturnArrowIcon } from "components/ReturnArrowIcon";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useTenant } from "hooks/useTenant";
import { useCallback, useState } from "react";
import { Control, useFieldArray, useWatch } from "react-hook-form";
import { TaskFormType } from "./types";

type Props = {
  control: Control<TaskFormType>;
  onDelete: (id: string) => void;
};

type AddDocumentControlProps = Omit<Props, "onDelete"> & {
  tooltipTitle?: string;
  ariaLabel?: string;
};

export const AddDocumentControl = ({
  control,
  tooltipTitle = "Attach document",
  ariaLabel = "Attach document",
}: AddDocumentControlProps) => {
  const { tenantId } = useTenant();
  const [addEditDialogState, setAddEditDialogState] =
    useState<AddEditDocumentDialogState>(defaultAddEditDocumentDialogState);

  const { append } = useFieldArray({
    control,
    name: "documents",
    keyName: "_id",
  });

  return (
    <>
      {tenantId && (
        <Tooltip title={tooltipTitle} arrow>
          <IconButton
            key="attachments"
            aria-label={ariaLabel}
            onClick={() => {
              setAddEditDialogState({
                ...defaultAddEditDocumentDialogState,
                open: true,
                mode: EditMode.ADD,
                step: FormState.UPLOAD,
              });
            }}
          >
            <AttachFileOutlinedIcon />
          </IconButton>
        </Tooltip>
      )}

      {addEditDialogState.open && tenantId && (
        <AddEditDocumentDialog
          tenantId={tenantId}
          dialogState={addEditDialogState}
          setDialogState={setAddEditDialogState}
          showFacilityPicker={false}
          onSubmit={(document, dialogState) => {
            if (dialogState.mode === "Add") {
              append(document);
            }
          }}
          refetchQueries={[]}
        />
      )}
    </>
  );
};

type FormDocument = TaskFormType["documents"][number];
export const DocumentsListControl = ({ control, onDelete }: Props) => {
  const { tenantId } = useTenant();

  const [addEditDialogState, setAddEditDialogState] =
    useState<AddEditDocumentDialogState>(defaultAddEditDocumentDialogState);
  const [previewDocument, setPreviewDocument] = useState<{
    title: string;
    url: string;
  } | null>(null);

  const { remove, update } = useFieldArray({
    control,
    name: "documents",
    keyName: "_id",
  });

  const documents = useWatch({
    control,
    name: "documents",
  });

  const [getDocumentDownloadLink] = useDocumentDownloadLink();

  const handlePreview = useCallback(
    async (document: FormDocument) => {
      const { data } = await getDocumentDownloadLink({
        variables: { id: document.id },
      });
      if (data?.getDocumentDownloadLink) {
        setPreviewDocument({
          title: document.title,
          url: data.getDocumentDownloadLink,
        });
      }
    },
    [getDocumentDownloadLink]
  );

  const handleDelete = (id: string) => {
    const index = documents.findIndex((document) => document.id === id);
    if (index !== -1) {
      remove(index);
      onDelete(id);
    }
  };
  const handleEdit = (document: TaskFormType["documents"][number]) => {
    setAddEditDialogState({
      ...defaultAddEditDocumentDialogState,
      open: true,
      mode: EditMode.EDIT,
      step: FormState.FORM,
      documentId: document.id,
      documentType: document.documentType,
      fileExtension: document.fileExtension,
      title: document.title,
    });
  };

  if (documents.length === 0) {
    return null;
  }

  return (
    <Stack>
      {documents.map((document) => (
        <DocumentListItem
          key={document.id}
          document={document}
          onDelete={() => handleDelete(document.id)}
          onEdit={() => handleEdit(document)}
          onPreview={() => handlePreview(document)}
        />
      ))}

      {addEditDialogState.open && tenantId && (
        <AddEditDocumentDialog
          tenantId={tenantId}
          dialogState={addEditDialogState}
          setDialogState={setAddEditDialogState}
          showFacilityPicker={false}
          onSubmit={(document, dialogState) => {
            const index = documents.findIndex(
              (doc) => doc.id === dialogState.documentId
            );
            if (index !== -1) {
              update(index, document);
            }
          }}
          refetchQueries={[]}
        />
      )}

      {previewDocument && (
        <Dialog
          open={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
          fullWidth
          maxWidth="lg"
          sx={{ "& .MuiDialog-paper": { height: "90vh" } }}
          title="Preview Document"
          noPrimaryAction
          closeIcon={<ReturnArrowIcon />}
        >
          <Typography variant="body2" fontStyle="italic" sx={{ pb: 2 }}>
            {previewDocument.title}
          </Typography>
          <DocumentPreview fileUrl={previewDocument.url} />
        </Dialog>
      )}
    </Stack>
  );
};
